// Privacy policy for TaskPogo. Drafted by the engineering team based on
// what the app actually collects and processes; should be reviewed by
// counsel before being relied upon.
//
// "TaskPogo" is left spelled out here rather than routed through
// Brand.PRODUCT_NAME: this page DEFINES it as a legal term in its opening
// paragraph, and a defined term reads oddly as an interpolation.

import { SUPPORT_EMAIL } from '../fleet-shared/Brand.mjs';

const EFFECTIVE_DATE = 'June 3, 2026';
// The address is Brand.mjs's; aliased so the page's existing
// {CONTACT_EMAIL} references read unchanged.
const CONTACT_EMAIL = SUPPORT_EMAIL;

const Privacy = () => {
  return (
    <div className="container py-4" style={{ maxWidth: 820 }}>
      <h1>Privacy Policy</h1>
      <p className="text-body-secondary">Effective date: {EFFECTIVE_DATE}</p>

      <p>
        This Privacy Policy explains what information TaskPogo
        (&ldquo;TaskPogo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;)
        collects when you use the TaskPogo website and service at{' '}
        <a href="https://www.taskpogo.com">www.taskpogo.com</a>, how we
        use it, and the choices you have. By using TaskPogo you agree to
        the practices described here.
      </p>

      <h2>Information we collect</h2>

      <h3>Account information</h3>
      <p>
        When you create an account we collect the email address you sign
        up with and a password (stored as a salted hash by Amazon Cognito,
        not by us in plaintext). We use the email address to identify
        your account, send verification and password-reset messages, and
        contact you about the service.
      </p>

      <h3>Content you provide</h3>
      <p>
        TaskPogo lets you create &ldquo;items&rdquo; (boats, airplanes, homes,
        vehicles, equipment, projects, etc.) and attach trips and routes, tasks,
        counters (e.g. engine hours, nautical miles, cycles), logbook entries,
        and images to them. We store this content so we can show it back to you,
        send the reminders you configure, and let people you authorize see it.
      </p>

      <h3>Location data</h3>
      <p>
        Trips and logbook entries can carry a <strong>route</strong>: an ordered
        list of waypoints, each with a name and, when you place it, a latitude
        and longitude. We store these as part of the trip or entry. Two things
        follow that are worth stating plainly.
      </p>
      <p>
        First, the <strong>places list is shared between all TaskPogo users</strong>.
        When you name and position a place - a harbour, an anchorage, a marina -
        that row, including its name, position and facilities, is visible to and
        editable by every signed-in user. It is a shared gazetteer by design.
        Your trips, routes and logbook entries are <em>not</em> shared; only the
        place rows they refer to are. Do not put anything private in a place
        name or its notes.
      </p>
      <p>
        Second, showing you a map and its weather means{' '}
        <strong>sending coordinates to third parties directly from your browser</strong>.
        When you look up a place by name, the text you type is sent to the{' '}
        <a href="https://photon.komoot.io/" target="_blank" rel="noreferrer noopener">
          Photon
        </a>{' '}
        geocoding service. When a map shows weather, the coordinates of your
        route are sent to the US National Weather Service, Environment and
        Climate Change Canada, NOAA, and the Canadian Hydrographic Service, and
        (for a small number of feeds that do not permit direct browser access)
        through a TaskPogo proxy to NDBC and aviationweather.gov. Map tiles come
        from OpenFreeMap. These requests are made by your browser as you use the
        map; each of those services receives your IP address and the coordinates
        requested, and handles them under its own privacy policy, not ours. We
        do not send them your name, email address, or account identifier.
      </p>
      <p>
        TaskPogo does not read your device&apos;s GPS or ask for browser
        location permission. Every position we hold is one you entered, placed,
        or picked.
      </p>

      <h3>Email subscriptions</h3>
      <p>
        You can subscribe one or more email addresses to receive periodic
        status digests for an item. We store those email addresses, the
        delivery schedule you choose, and a record of when messages were
        sent. Recipients can unsubscribe at any time using the link in
        every digest; unsubscribing does not require a TaskPogo account.
      </p>

      <h3>Uploaded files</h3>
      <p>
        Images and other files you attach to items, tasks, or logbook
        entries are stored in Amazon S3 under your account. Thumbnails
        are generated automatically. We do not scan the contents of your
        files for advertising or profiling purposes.
      </p>

      <h3>Technical information</h3>
      <p>
        Like most web services, our hosting and infrastructure providers
        (AWS Amplify, AppSync, CloudFront, S3, SES) record technical
        information such as IP address, browser user agent, and request
        timestamps in their server logs. We use these logs for security,
        debugging, and abuse prevention.
      </p>

      <h2>What we do <em>not</em> do</h2>
      <ul>
        <li>We do not sell your personal information.</li>
        <li>
          We do not show third-party advertising and we do not use
          third-party advertising or analytics trackers to profile you.
        </li>
        <li>
          We do not use the content of your items, tasks, logbook, or
          uploaded files to train machine-learning models.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>To provide the service - showing your items, tasks, and logbook back to you.</li>
        <li>To send email digests, reminders, and account messages you have asked for.</li>
        <li>To keep the service secure and prevent abuse.</li>
        <li>To respond to support requests you send us.</li>
        <li>To comply with legal obligations.</li>
      </ul>

      <h2>Service providers</h2>
      <p>
        TaskPogo runs on Amazon Web Services. Your account, content, and
        emails are stored and processed in AWS services (Cognito,
        AppSync, DynamoDB, S3, SES, Lambda, SQS, CloudFront) operating in
        the United States. AWS processes this data on our behalf under
        its standard data processing terms. We do not share your data
        with other third parties except as described in this policy or
        as required by law.
      </p>

      <h2>Security</h2>
      <p>
        Account passwords are managed by Amazon Cognito and stored as
        salted hashes. Data in transit is protected by TLS. Data at rest
        in AWS is encrypted using AWS-managed keys. No system is
        perfectly secure; if you believe your account has been
        compromised, please contact us immediately.
      </p>

      <h2>Retention and deletion</h2>
      <p>
        We keep your account and content for as long as your account is
        active. You can delete individual items, tasks, logbook entries,
        and files at any time from within the app. To delete your entire
        account and all associated content, email us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from the
        address associated with your account. Backups and server logs
        may retain residual copies for a limited period before they are
        rotated out.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>
          You can review and edit the content in your account at any
          time by signing in at{' '}
          <a href="https://www.taskpogo.com/">www.taskpogo.com</a>.
        </li>
        <li>
          Email recipients can unsubscribe from any item digest using
          the link in the message.
        </li>
        <li>
          You can request account deletion or a copy of the personal
          data we hold about you by emailing{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </li>
      </ul>

      <h2>Children</h2>
      <p>
        TaskPogo is not directed to children under 13 and we do not
        knowingly collect personal information from them. If you believe
        a child has signed up, please contact us so we can remove the
        account.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy as the service evolves. When we make
        material changes, we will update the effective date above and,
        where appropriate, notify you by email.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this policy or how we handle your data? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
};

export default Privacy;
